import { createLumeFxRestClient } from '@/services/LumeFxService';
import { addMarketFull } from '@/services/MarketService';
import { useAppSelector } from '@/state/hooks';
import { selectCredentials } from '@/state/reducers/authSlice';
import { OrderPrefillValues } from '@/state/reducers/orderSlice';
import { selectSettings } from '@/state/reducers/settingSlice';
import { LumeFxOrderType, LumeFxSide, LumeFxTimeInForce } from '@/types/lumeFx';
import { useDidUpdate } from '@/utils/hooks/useDidUpdate';
import useLanguage from '@/utils/hooks/useLanguage';
import useOcoLimitOrder from '@/utils/hooks/useOcoLimitOrder';
import { invalidStopPriceMessage } from '@/utils/hooks/useOrderBook';
import { UseOrderExecutionReturn } from '@/utils/hooks/useOrderExecution';
import useTotals from '@/utils/hooks/useTotals';
import useTradeFeePreview from '@/utils/hooks/useTradeFeePreview';
import { useLucera } from '@/utils/providers/LuceraProvider';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDebouncedValue } from '@mantine/hooks';
import BigNumber from 'bignumber.js';
import { Fragment, useCallback, useEffect, useMemo } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import FeePreview from '../common/FeePreview';
import Tooltip from '../common/Tooltip';
import ExecutionInputController from '../inputs/ExecutionInputController';
import NumberInputDisplay from '../inputs/NumberInputDisplay';
import RHFNumberInput from '../inputs/RHFNumberInput';
import Select from '../inputs/Select';
import SlippageInput from '../inputs/SlippageInput';
import { getStopPrice } from './StopOrderForm';
import { BalancePreview } from './components/BalancePreview';
import FormBody from './components/FormBody';
import FormCurrency from './components/FormCurrency';
import FormFooter from './components/FormFooter';
import FormOrderTypeSwitch from './components/FormOrderTypeSwitch';
import FormSubmit from './components/FormSubmit';
import Totals from './components/Totals';
import { OcoOrderFormInput, OcoOrderFormValues, ocoOrderSchema } from './schema/ocoOrderSchema';

export const orderTypeOptions: { label: string; value: LumeFxOrderType }[] = [
    { label: 'STOP MARKET', value: LumeFxOrderType.Stop },
    { label: 'STOP LIMIT', value: LumeFxOrderType.StopLimit },
    { label: 'LIMIT', value: LumeFxOrderType.Limit }
];

export const durationOptions: { label: keyof typeof LumeFxTimeInForce; value: LumeFxTimeInForce }[] = [
    { label: 'DAY', value: LumeFxTimeInForce.DAY },
    { label: 'GTC', value: LumeFxTimeInForce.GTC }
];

const defaultValues: Partial<OcoOrderFormInput> = {
    order1: {
        type: orderTypeOptions[2],
        limitPrice: undefined,
        quantity: null
    } as any,
    order2: {
        type: orderTypeOptions[0],
        stopPrice: undefined,
        stopLimitPrice: undefined,
        quantity: null
    } as any,
    duration: durationOptions[0]
};

interface OcoOrderFormProps extends UseOrderExecutionReturn {
    parentSize?: number;
    prefillValues?: OrderPrefillValues;
    onClose?: () => void;
    closeOnSubmit?: boolean;
}

const luceraRestURL = window.config?.lucera?.rest;

function OcoOrderForm(props: OcoOrderFormProps) {
    const { parentSize = 0, prefillValues, onClose, closeOnSubmit = false, ...orderExecutionProps } = props;
    const {
        mode,
        ccy1,
        ccy2,
        sideState,
        currencyState,
        priceCurrency,
        activeCurrencyConfig,
        orderBook,
        instrument,
        pairMapToUse,
        minOrderSize,
        orderDecimals,
        ccy1Config,
        ccy2Config,
        isCcy2Order
    } = orderExecutionProps;

    const lang = useLanguage();
    const settings = useAppSelector(selectSettings);
    const credentials = useAppSelector(selectCredentials);

    const { jwt } = useLucera();

    const lumeFxRestClient = createLumeFxRestClient({
        baseUrl: luceraRestURL,
        authorization: jwt
    });

    const [side, setSide] = sideState;
    const [currency, setCurrency] = currencyState;

    const { formatPrice, pip_size, price_decimals, defaultSlippage } = instrument;
    const { asks, bids, bestAsk, bestBid, bestPrice, worstAsk, worstBid } = orderBook;

    const form = useForm<OcoOrderFormInput>({
        defaultValues,
        mode: 'onChange',
        resolver: yupResolver(
            ocoOrderSchema({
                priceOptions: {
                    side,
                    bestAsk,
                    bestBid,
                    formattedBestAsk: formatPrice(bestAsk),
                    formattedBestBid: formatPrice(bestBid),
                    ccy2Order: isCcy2Order
                },
                quantityOptions: {
                    min_order_size: minOrderSize,
                    order_decimals: orderDecimals,
                    currencyOut: currency,
                    currencyPair: pairMapToUse?.show
                }
            })
        )
    });

    const totals = useTotals({
        form,
        name: 'order1.quantity',
        pairMap: pairMapToUse,
        side,
        ccy1: instrument?.type === 'Future' ? instrument?.config?.ccy2 || '' : ccy1,
        ccy2: instrument?.type === 'Future' ? instrument?.config?.ccy2 || '' : ccy2,
        ccy2Order: isCcy2Order,
        bids,
        asks
    });

    const {
        watch,
        reset,
        setValue,
        setError,
        clearErrors,
        handleSubmit,
        formState: { isSubmitting, isValid }
    } = form;

    const [order1, order2] = watch(['order1', 'order2']);
    const [debouncedQuantity] = useDebouncedValue(order1?.quantity || order2?.quantity, 300);

    const { spotFeesApi, cfdFeesApi } = useTradeFeePreview({
        ...orderExecutionProps,
        side,
        isCfd: instrument.type === 'FX' ? false : true,
        debouncedQuantity: debouncedQuantity || null
    });

    const { data: spotFees } = spotFeesApi;
    const { data: cfdFees } = cfdFeesApi;

    const fees = useMemo(() => spotFees || cfdFees, [spotFees, cfdFees]);

    const onSubmit = async (data: OcoOrderFormInput, e) => {
        const dataValidated = data as OcoOrderFormValues;
        if (dataValidated.order1.quantity && dataValidated.order2.quantity && credentials) {
            if (closeOnSubmit) onClose?.();

            const linkId = crypto.randomUUID();
            lumeFxRestClient.placeOrder({
                Account: '123',
                Symbol: pairMapToUse?.celer,
                TimeInForce: dataValidated.duration.value as LumeFxTimeInForce,
                OrdType: LumeFxOrderType.Limit,
                Price: (order1?.limitPrice || 0).toString(),
                OrderQty: (order1?.quantity || 0).toString(),
                ClOrdID: crypto.randomUUID(),
                Side: side,
                ClOrdLinkID: linkId
            });
            lumeFxRestClient.placeOrder({
                Account: '123',
                Symbol: pairMapToUse?.celer,
                TimeInForce: dataValidated.duration.value as LumeFxTimeInForce,
                OrdType: LumeFxOrderType.Limit,
                Price: (order2?.stopPrice || 0).toString(),
                OrderQty: (order2?.quantity || 0).toString(),
                ClOrdID: crypto.randomUUID(),
                Side: side,
                ClOrdLinkID: linkId
            });
        }
    };

    const onError = (errors) => console.error(errors);

    const { limitPriceTooltip, isRestingLimitOrder } = useOcoLimitOrder(form, props, 'order1.');

    useEffect(() => {
        setValue('order2.stopPrice', getStopPrice(bestBid, bestAsk, side, isCcy2Order, price_decimals), {
            shouldValidate: true
        });
    }, [side]);

    useEffect(() => {
        const isCcy1Buy = side === LumeFxSide.BUY && !isCcy2Order;
        const isCcy2Buy = side === LumeFxSide.BUY && isCcy2Order;
        const isCcy1Sell = side === LumeFxSide.SELL && !isCcy2Order;
        const isCcy2Sell = side === LumeFxSide.SELL && isCcy2Order;

        if (order2?.stopPrice && bestBid && bestAsk) {
            if (isCcy1Buy || isCcy2Sell) {
                const message = invalidStopPriceMessage(side, formatPrice(bestBid), isCcy2Order);
                if (+order2.stopPrice <= bestBid) {
                    setError('order2.stopPrice', { type: 'stopPriceConstraint', message });
                }
            } else if (isCcy2Buy || isCcy1Sell) {
                const message = invalidStopPriceMessage(side, formatPrice(bestAsk), isCcy2Order);
                if (+order2.stopPrice >= bestAsk) {
                    setError('order2.stopPrice', { type: 'stopPriceConstraint', message });
                }
            } else {
                clearErrors('order2.stopPrice');
            }
        }
    }, [bestBid, bestAsk, isCcy2Order, side]);

    const resetDefault = useCallback(() => {
        // resets all fields, set limit price to default worst price
        let quantity: number | null = null;
        let order1LimitPrice = worstAsk;

        if (side === LumeFxSide.BUY && asks) order1LimitPrice = worstAsk;
        else if (side === LumeFxSide.SELL && bids) order1LimitPrice = worstBid;

        if (prefillValues) {
            quantity = +Math.abs(prefillValues.netQtyPosition).toFixed(
                isCcy2Order ? ccy2Config.order_decimals : ccy1Config.order_decimals
            );
        }

        const resetValues: typeof defaultValues = {
            ...defaultValues,
            order1: {
                type: orderTypeOptions[2],
                limitPrice: order1LimitPrice,
                quantity
            } as any,
            order2: {
                type: orderTypeOptions[0],
                stopPrice: getStopPrice(bestBid, bestAsk, side, isCcy2Order, price_decimals),
                stopLimitPrice: undefined,
                quantity,
                slippage: defaultSlippage
            } as any
        };

        if ([LumeFxTimeInForce.DAY, LumeFxTimeInForce.GTC].includes(settings.trading.defaultStopOrderDuration.value)) {
            resetValues.duration = settings.trading.defaultStopOrderDuration;
        }

        reset(resetValues);
    }, [
        prefillValues,
        defaultValues,
        side,
        isCcy2Order,
        price_decimals,
        bestAsk,
        bestBid,
        worstAsk,
        worstBid,
        settings,
        defaultSlippage
    ]);

    useDidUpdate(() => {
        if (credentials && debouncedQuantity && debouncedQuantity > 0) addMarketFull(pairMapToUse.celer);
    }, [debouncedQuantity]);

    useDidUpdate(() => resetDefault(), [currency, side]);
    useDidUpdate(() => {
        if (prefillValues) {
            const sideToSet = (prefillValues.netQtyPosition || 0) < 0 ? LumeFxSide.SELL : LumeFxSide.BUY;
            if (side !== sideToSet) setSide(sideToSet); // This will trigger a reset default already
            else resetDefault();
        }
    }, [prefillValues]);

    useEffect(() => {
        if (prefillValues) {
            const sideToSet = (prefillValues.netQtyPosition || 0) < 0 ? LumeFxSide.SELL : LumeFxSide.BUY;
            if (side !== sideToSet) setSide(sideToSet); // This will trigerr a reset default already
            else resetDefault();
        } else resetDefault();
    }, [pairMapToUse.celer]);

    return (
        <FormProvider {...form}>
            <form
                onSubmit={handleSubmit(onSubmit, onError)}
                className="relative flex flex-col h-full w-full text-brand-text">
                <FormBody side={side} mode={mode === 'popup' ? 'Modal' : 'Module'}>
                    <FormOrderTypeSwitch initialValue={side} onChange={setSide} />
                    <FormCurrency {...props} currency={currency} setCurrency={setCurrency} />
                    <div className="flex flex-wrap gap-3">
                        <OrderForm
                            side={side}
                            title={`${lang.modules.order} 1`}
                            idPrefix="order1"
                            pip_size={pip_size}
                            min_order_size={minOrderSize}
                            step={activeCurrencyConfig.increments}
                            currency={activeCurrencyConfig.show}
                            priceCurrency={priceCurrency}
                            limitPriceTooltip={limitPriceTooltip}
                            isRestingLimitOrder={isRestingLimitOrder}
                        />
                        <OrderForm
                            side={side}
                            title={`${lang.modules.order} 2`}
                            idPrefix="order2"
                            pip_size={pip_size}
                            min_order_size={minOrderSize}
                            step={activeCurrencyConfig.increments}
                            currency={activeCurrencyConfig.show}
                            priceCurrency={priceCurrency}
                            defaultSlippage={defaultSlippage}
                        />
                    </div>
                    <ExecutionInputController name="duration" label={`${lang.orderForm.duration} *`}>
                        <Select type="execution" options={durationOptions} />
                    </ExecutionInputController>
                    <Totals {...totals} />
                    {instrument.type === 'FX' && order1?.quantity && (
                        <FeePreview
                            feesDetails={spotFees}
                            isLoadingFeesDetails={spotFeesApi.isLoading || spotFeesApi.isValidating}
                            feesDetailsError={spotFeesApi.error}
                        />
                    )}
                    {instrument.type !== 'FX' && order1?.quantity && (
                        <FeePreview
                            feesDetails={cfdFees}
                            isLoadingFeesDetails={cfdFeesApi.isLoading || cfdFeesApi.isValidating}
                            feesDetailsError={cfdFeesApi.error}
                        />
                    )}
                    {totals.total !== '-' && debouncedQuantity && (
                        <BalancePreview
                            fees={fees}
                            totals={totals}
                            quantity={debouncedQuantity}
                            parentSize={parentSize}
                            {...orderExecutionProps}
                        />
                    )}
                </FormBody>
                <FormFooter mode={mode === 'popup' ? 'Modal' : 'Module'}>
                    <FormSubmit
                        side={side}
                        isValid={isValid}
                        disabled={isSubmitting || !isValid || totals.disableSubmit || !instrument.type}
                        noInstrumentConfig={!instrument.type}
                        noSettlementDate={!orderBook.settlementDate}
                        className={mode === 'popup' ? 'left-6' : ''}
                    />
                </FormFooter>
            </form>
        </FormProvider>
    );
}

export default OcoOrderForm;

export const OrderForm = ({
    side,
    title,
    idPrefix,
    pip_size,
    min_order_size,
    defaultSlippage,
    step,
    priceStep,
    currency,
    priceCurrency,
    limitPriceTooltip,
    isRestingLimitOrder
}: any) => {
    const lang = useLanguage();
    const { register, watch, setValue } = useFormContext();
    const { value: type } = watch(`${idPrefix}.type`);
    const [slippage, stopPrice] = watch([`order2.slippage`, `order2.stopPrice`]);
    const order1Quantity = watch('order1.quantity');

    const isStopType = [LumeFxOrderType.StopLimit, LumeFxOrderType.Stop].includes(type);

    const filteredOrderTypeOptions = useMemo(() => {
        if (idPrefix === 'order2') {
            return orderTypeOptions.filter((option) => option.value !== LumeFxOrderType.Limit);
        } else {
            return orderTypeOptions;
        }
    }, [idPrefix]);

    const stopLimitPrice = useMemo(() => {
        if (idPrefix === 'order2' && type === LumeFxOrderType.StopLimit && stopPrice) {
            const slip = BigNumber(+(slippage || 0)).dividedBy(Math.pow(10, pip_size || 0));
            const stop = +(stopPrice || 0);
            if (side === LumeFxSide.BUY) return BigNumber(stop).plus(slip).toNumber();
            if (side === LumeFxSide.SELL) return BigNumber(stop).minus(slip).toNumber();
            return stop;
        }
        return 0;
    }, [stopPrice, slippage, side]);

    useEffect(() => {
        if (idPrefix === 'order2' && order1Quantity) {
            setValue('order2.quantity', order1Quantity, { shouldValidate: true });
        }
    }, [order1Quantity]);

    useEffect(() => {
        if (idPrefix === 'order2' && stopLimitPrice) {
            setValue('order2.stopLimitPrice', stopLimitPrice, { shouldValidate: true });
        }
    }, [stopLimitPrice]);

    return (
        <div className="relative flex-1 flex flex-col gap-3 p-2 border border-brand-neutral-700 rounded-md">
            <div className="-top-2 text-xs absolute bg-brand-background text-brand-neutral-400 px-1">{title}</div>
            <ExecutionInputController
                name={`${idPrefix}.type`}
                label={`${lang.orderForm.orderType} *`}
                disabled={idPrefix === 'order1'}>
                <Select type="execution" options={filteredOrderTypeOptions} />
            </ExecutionInputController>
            {isStopType && (
                <RHFNumberInput
                    label={`${lang.orderForm.stopPrice} *`}
                    placeholder={lang.orderForm.stopPrice}
                    {...register(`${idPrefix}.stopPrice`)}
                    step={BigNumber(1)
                        .dividedBy(Math.pow(10, pip_size || 0))
                        .toNumber()}
                    currency={priceCurrency.show}
                />
            )}
            {!isStopType && (
                <Tooltip content={limitPriceTooltip || ''} className="-top-16 right-0 w-2/3">
                    <RHFNumberInput
                        label={`${lang.orderForm.limitPrice} *`}
                        placeholder={lang.orderForm.limitPrice}
                        {...register(`${idPrefix}.limitPrice`)}
                        disabled={!isRestingLimitOrder}
                        className={{
                            'text-brand-red': limitPriceTooltip,
                            'text-brand-text': !limitPriceTooltip
                        }}
                        step={BigNumber(1)
                            .dividedBy(Math.pow(10, pip_size || 0))
                            .toNumber()}
                        currency={priceCurrency.show}
                    />
                </Tooltip>
            )}
            {type === LumeFxOrderType.StopLimit && idPrefix === 'order2' && (
                <Fragment>
                    <SlippageInput
                        {...register(`${idPrefix}.slippage`)}
                        label={lang.orderForm.slippage}
                        placeholder={lang.orderForm.slippage}
                        defaultSlippage={defaultSlippage}
                        step={0.1}
                    />
                    <Tooltip
                        content="The Stop Limit price is placed as the Stop Price +/- Slippage"
                        className="max-w-fit">
                        <ExecutionInputController
                            name={`${idPrefix}.stopLimitPrice`}
                            label={`${lang.orderForm.stopLimitPrice} *`}
                            value={stopLimitPrice}>
                            <NumberInputDisplay placeholder="Stop Limit Price" currency={priceCurrency.show} disabled />
                        </ExecutionInputController>
                    </Tooltip>
                </Fragment>
            )}
            <RHFNumberInput
                {...register(`${idPrefix}.quantity`)}
                label={`${lang.orderForm.quantity} *`}
                placeholder={lang.orderForm.quantity}
                min={min_order_size}
                step={step}
                disabled={idPrefix === 'order2'}
                currency={currency}
            />
        </div>
    );
};
